// @flow
import React, { memo } from 'react';
import styled from 'styled-components';
import { H6 } from '@finect/tabular-core/Headings';
import { Small } from '@finect/tabular-core/Small';
import { Icon } from '@finect/tabular-components/Icon';
import { Button } from '@finect/tabular-core/Button';
import { MapPin } from '@finect/tabular-icons/mapPin';
import { ExternalLink } from '@finect/tabular-icons/externalLink';
import { maxMediaQueries, minMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { CardBlock, CardRow, CardListItem, CardListItemLink, EAGER_LIST_ICONS } from '../../../components';
import type { CompanyType } from '../../../types';

const isValidUrl = url => url && url.length > 0 && url.indexOf('http') === 0;

const CompanyName = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 40%;
  min-height: 66px;
  padding-left: 78px;
  padding-right: 24px;

  h6 {
    /* stylelint-disable */
    padding-bottom: 0 !important;
    /* stylelint-enable */
  }

  ${maxMediaQueries.tablet`
    width: 50%;
  `};

  ${maxMediaQueries.phablet`
    width: 100%;
    padding-left: 78px;
    padding-right: 0;
  `};

  ${minMediaQueries.miniDesktop`
    flex: 1;
  `};
`;

const CompanyMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 60%;

  ${maxMediaQueries.tablet`
    width: 50%;
  `};

  ${maxMediaQueries.phablet`
    width: 100%;
    margin-top: 15px;
  `};
`;

const Locations = styled.div`
  position: relative;
  padding-left: 19px;
  padding-right: 24px;

  svg {
    position: absolute;
    top: 5px;
    left: 0;
    opacity: 0.6;
    margin-right: 5px;
    color: ${({ theme }) => theme.colors.fontL};
  }
`;

const More = styled.div`
  color: ${({ theme }) => theme.mainColor};
  text-align: right;

  &:hover {
    opacity: 0.9;
  }

  svg {
    position: relative;
    top: 2px;
    margin-left: 3px;
  }
`;

type Props = {
  url?: string | null,
  children: any
};

const ExternalCompany = ({ url = null, children }: Props) => {
  if (url) {
    return (
      <CardListItemLink href={url} target="_blank">
        {children}
      </CardListItemLink>
    );
  }
  return <CardListItem>{children}</CardListItem>;
};

const renderLocations = locations =>
  (locations.length > 1 ?
    locations.length > 2 ? (
      <span>
        {locations.length} localizaciones
        <Small light>
          {locations[0]}, {locations[1]} y {locations.length - 2} más.
        </Small>
      </span>
    ) : (
      <span>
        {locations.length} localizaciones
        <Small light>{locations.join(', ')}.</Small>
      </span>
    )
    :
    locations[0] || 'Sin ubicación'
  );

type CardProps = CompanyType & { index?: number };

const CompanyCardComponent = (props: CardProps) => {
  const {
    idProfile, displayName, icon, externalUrl, urlFriendly, companyType, locations = [], userLocation, index = 0
  } = props;
  const lazyIcon = index >= EAGER_LIST_ICONS;
  const url = urlFriendly
    ? `https://${process.env.FINECT_BASEURL || 'www.finect.com'}/grupos/${urlFriendly}${
      userLocation !== -1 ? `?location=${userLocation}` : ''
    }`
    : isValidUrl(externalUrl) ? externalUrl : null;
  const capitalizedLocations = locations.map(
    location => `${location[0].toUpperCase()}${location.slice(1).toLowerCase()}`
  );

  const contact = (event) => {
    event.preventDefault();
    location.href = `https://${
      process.env.FINECT_BASEURL || 'www.finect.com'
    }/mensajes/nuevo?members=${idProfile}&message=Estoy interesado en sus servicios de asesoramiento.`;
  };

  return (
    <div>
      {urlFriendly ? (
        <CardBlock>
          <CardRow href={url}>
            <Icon alt={displayName} image={icon} isCompany lazyLoad={lazyIcon} />
            <CompanyName>
              <H6>{displayName}</H6>
              <Small light>{companyType || '–'}</Small>
            </CompanyName>
            <CompanyMore>
              <Locations>
                <MapPin size="14" />
                {renderLocations(capitalizedLocations)}
              </Locations>
              <Button width="auto" variant="small" onClick={contact}>
                Contactar
              </Button>
            </CompanyMore>
          </CardRow>
        </CardBlock>
      ) : (
        <ExternalCompany url={url}>
          <Icon alt={displayName} type="company" />
          <CompanyName>
            <H6>{displayName}</H6>
            <Small light>{companyType || '–'}</Small>
          </CompanyName>
          <CompanyMore>
            <Locations>
              <MapPin size="14" />
              {renderLocations(capitalizedLocations)}
            </Locations>
            <More>
              {url ? (
                <span>
                  Saber más <ExternalLink size="16" strokeWidth="1.8" />
                </span>
              ) : null}
            </More>
          </CompanyMore>
        </ExternalCompany>
      )}
    </div>
  );
};

export const CompanyCard: React$AbstractComponent<CardProps, mixed> = memo(CompanyCardComponent);
